import React, { useEffect, useRef, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Upload,
  Loader2,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Type,
  Hash,
  List,
  ListChecks,
  ToggleLeft,
  Link2,
  Layers,
  X,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

const TYPE_META = {
  text: { label: "Text", icon: Type },
  number: { label: "Number", icon: Hash },
  dropdown: { label: "Dropdown", icon: List },
  multiselect: { label: "Multi-select", icon: ListChecks },
  boolean: { label: "Yes / No", icon: ToggleLeft },
  url: { label: "URL", icon: Link2 },
  option: { label: "Option (Std/Opt/NA)", icon: Layers },
};

/**
 * CsvNewPtSheet
 * Lets the user create a brand-new product type by dropping a CSV.
 * The backend infers columns + types from the CSV; the user can tweak before saving.
 */
export default function CsvNewPtSheet({
  open,
  onOpenChange,
  manufacturerId,
  manufacturerName,
  onCreated,
}) {
  const [stage, setStage] = useState("upload"); // upload | analyzing | review
  const [csvFile, setCsvFile] = useState(null);
  const [analysis, setAnalysis] = useState(null); // backend response
  const [name, setName] = useState("");
  const [columns, setColumns] = useState([]);
  // Override mapping: which column should populate the row Name / Model # fields.
  // Default is null = use the backend-detected reserved columns.
  // Value is the column KEY (or "" for "use auto-detected").
  const [nameOverrideKey, setNameOverrideKey] = useState("");
  const [modelOverrideKey, setModelOverrideKey] = useState("");
  const [specPdf, setSpecPdf] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStage("upload");
      setCsvFile(null);
      setAnalysis(null);
      setName("");
      setColumns([]);
      setNameOverrideKey("");
      setModelOverrideKey("");
      setSpecPdf(null);
      setSaving(false);
      setDragOver(false);
    }
  }, [open]);

  const onFileChosen = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file.");
      return;
    }
    setCsvFile(file);
    setStage("analyzing");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/csv/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAnalysis(data);
      setName(data.suggested_pt_name || "");
      setColumns(
        (data.columns || []).map((c) => ({
          key: c.key,
          label: c.label,
          type: c.type,
          options: c.options || [],
        }))
      );
      setStage("review");
    } catch (e) {
      toast.error(formatApiError(e, "Could not analyze CSV"));
      setStage("upload");
      setCsvFile(null);
    }
  };

  const onPdfUpload = async (file) => {
    if (!file) return;
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pdfs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSpecPdf({ id: data.id, filename: data.filename });
      toast.success("Spec sheet attached");
    } catch (e) {
      toast.error(formatApiError(e, "Could not upload spec sheet"));
    } finally {
      setUploadingPdf(false);
    }
  };

  const setColumnType = (idx, type) => {
    setColumns((prev) =>
      prev.map((c, i) =>
        i === idx
          ? {
              ...c,
              type,
              options:
                type === "dropdown" || type === "multiselect"
                  ? c.options || []
                  : [],
            }
          : c
      )
    );
  };

  const setColumnLabel = (idx, label) => {
    setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, label } : c)));
  };

  const removeColumn = (idx) => {
    setColumns((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Give this product type a name");
      return;
    }
    if (columns.length === 0) {
      toast.error("Keep at least one column");
      return;
    }
    setSaving(true);
    try {
      // Apply per-row overrides if the user picked a different source column for
      // name or model_number. We DROP the source column from attributes so it's
      // not stored twice.
      const remappedRows = (analysis?.rows || []).map((r) => {
        const next = { ...r, attributes: { ...(r.attributes || {}) } };
        if (nameOverrideKey) {
          const v = next.attributes[nameOverrideKey];
          if (v !== undefined && v !== null && v !== "") {
            next.name = String(Array.isArray(v) ? v.join(", ") : v);
            delete next.attributes[nameOverrideKey];
          }
        }
        if (modelOverrideKey) {
          const v = next.attributes[modelOverrideKey];
          if (v !== undefined && v !== null && v !== "") {
            next.model_number = String(
              Array.isArray(v) ? v.join(", ") : v
            );
            delete next.attributes[modelOverrideKey];
          }
        }
        return next;
      });
      // Filter out the override-source columns from the schema too
      const filteredColumns = columns.filter(
        (c) => c.key !== nameOverrideKey && c.key !== modelOverrideKey
      );
      const payload = {
        name: name.trim(),
        description: "",
        columns: filteredColumns.map((c) => ({
          key: c.key,
          label: c.label,
          type: c.type,
          options: c.options || [],
          required: false,
        })),
        aliases: [],
        default_spec_sheet_pdf_id: specPdf?.id || null,
        rows: remappedRows,
      };
      const { data } = await api.post(
        `/manufacturers/${manufacturerId}/product-types/from-csv`,
        payload
      );
      toast.success(
        `Created "${data.product_type.name}" with ${data.products_created} row${
          data.products_created === 1 ? "" : "s"
        }`
      );
      onCreated?.(data.product_type);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e, "Could not save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-4xl p-0 flex flex-col"
        data-testid="csv-new-pt-sheet"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="text-[11px] uppercase tracking-widest text-amber-600 font-mono-data">
            New product type from CSV
          </div>
          <SheetTitle className="font-display tracking-tight truncate">
            {manufacturerName}
          </SheetTitle>
          <SheetDescription>
            Drop a CSV (from Gemini, Excel, anywhere) — the app reads the
            headers, infers column types from your data, and previews the
            product type before you save.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {stage === "upload" && (
            <UploadStage
              dragOver={dragOver}
              setDragOver={setDragOver}
              onFileChosen={onFileChosen}
              fileInputRef={fileInputRef}
            />
          )}
          {stage === "analyzing" && <AnalyzingStage file={csvFile} />}
          {stage === "review" && analysis && (
            <ReviewStage
              name={name}
              setName={setName}
              columns={columns}
              setColumnType={setColumnType}
              setColumnLabel={setColumnLabel}
              removeColumn={removeColumn}
              rows={analysis.rows}
              rowCount={analysis.row_count}
              specPdf={specPdf}
              uploadingPdf={uploadingPdf}
              onPdfUpload={onPdfUpload}
              onRemoveSpecPdf={() => setSpecPdf(null)}
              fileName={csvFile?.name || ""}
              detectedNameHeader={analysis.detected_name_header}
              detectedModelHeader={analysis.detected_model_header}
              nameOverrideKey={nameOverrideKey}
              setNameOverrideKey={setNameOverrideKey}
              modelOverrideKey={modelOverrideKey}
              setModelOverrideKey={setModelOverrideKey}
            />
          )}
        </div>

        <SheetFooter className="border-t border-border px-6 py-4 bg-white flex-row items-center justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
            data-testid="csv-new-pt-cancel"
          >
            Cancel
          </Button>
          {stage === "review" && (
            <Button
              onClick={save}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-sm font-semibold"
              data-testid="csv-new-pt-save"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Create with {analysis?.row_count || 0} row
              {analysis?.row_count === 1 ? "" : "s"}
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function UploadStage({ dragOver, setDragOver, onFileChosen, fileInputRef }) {
  return (
    <div
      className={`p-10 flex flex-col items-center justify-center min-h-[400px]`}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) onFileChosen(f);
      }}
    >
      <div
        className={`w-full max-w-xl border-2 border-dashed rounded-sm p-10 text-center transition-colors ${
          dragOver
            ? "border-amber-500 bg-amber-50"
            : "border-border bg-slate-50/50"
        }`}
      >
        <Upload className="w-12 h-12 mx-auto text-amber-600" />
        <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-slate-900">
          Drop your CSV here
        </h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
          The first row must be column headers. Reserved headers:{" "}
          <code className="bg-slate-100 px-1 rounded">name</code>,{" "}
          <code className="bg-slate-100 px-1 rounded">model_number</code>. For
          columns with multiple values per row use a pipe — e.g.{" "}
          <code className="bg-slate-100 px-1 rounded">3000K|4000K|5000K</code>.
        </p>
        <div className="mt-6">
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            data-testid="csv-new-pt-choose-file"
          >
            <Upload className="w-4 h-4 mr-2" /> Choose file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFileChosen(f);
              e.target.value = "";
            }}
          />
        </div>
      </div>
    </div>
  );
}

function AnalyzingStage({ file }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-10 text-center min-h-[400px]">
      <div className="relative">
        <div className="w-20 h-20 rounded-sm bg-slate-900 flex items-center justify-center">
          <FileText className="w-10 h-10 text-amber-400" />
        </div>
        <div className="absolute -bottom-1 -right-1 bg-amber-500 text-slate-900 rounded-sm p-1.5">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        </div>
      </div>
      <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-slate-900">
        Reading <span className="text-amber-600">{file?.name}</span>…
      </h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm">
        Inferring column types from your data — multiselect, boolean,
        dropdown, number, text, url.
      </p>
      <div className="mt-6 byrd-progress w-64" />
    </div>
  );
}

function ReviewStage({
  name,
  setName,
  columns,
  setColumnType,
  setColumnLabel,
  removeColumn,
  rows,
  rowCount,
  specPdf,
  uploadingPdf,
  onPdfUpload,
  onRemoveSpecPdf,
  fileName,
  detectedNameHeader,
  detectedModelHeader,
  nameOverrideKey,
  setNameOverrideKey,
  modelOverrideKey,
  setModelOverrideKey,
}) {
  // Effective mapping summary string for the banner
  const effectiveNameSrc = nameOverrideKey
    ? columns.find((c) => c.key === nameOverrideKey)?.label
    : detectedNameHeader || "model number (fallback)";
  const effectiveModelSrc = modelOverrideKey
    ? columns.find((c) => c.key === modelOverrideKey)?.label
    : detectedModelHeader || "(none — rows will have no model #)";
  const mappingLooksOff = !detectedNameHeader && !nameOverrideKey;

  return (
    <div className="px-6 py-6 space-y-6">
      <div
        className={`border rounded-sm p-3 flex items-start gap-2 ${
          mappingLooksOff
            ? "bg-amber-50 border-amber-200"
            : "bg-emerald-50 border-emerald-200"
        }`}
      >
        {mappingLooksOff ? (
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-700 shrink-0" />
        ) : (
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0" />
        )}
        <div
          className={`text-xs ${
            mappingLooksOff ? "text-amber-800" : "text-emerald-800"
          }`}
        >
          <strong>{fileName}</strong> parsed cleanly — {rowCount} row
          {rowCount === 1 ? "" : "s"} ready to import.
          {mappingLooksOff && (
            <span>
              {" "}
              No <code className="bg-white/60 px-1 rounded">name</code> column
              detected — pick one below or rows will be named after the model #.
            </span>
          )}
        </div>
      </div>

      {/* Name */}
      <div>
        <Label className="text-xs uppercase tracking-wider">
          Product type name
        </Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-sm mt-1"
          data-testid="csv-new-pt-name-input"
        />
      </div>

      {/* Column mapping for reserved fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider">
            Row "Name" comes from
          </Label>
          <Select
            value={nameOverrideKey || "_auto"}
            onValueChange={(v) => setNameOverrideKey(v === "_auto" ? "" : v)}
          >
            <SelectTrigger className="rounded-sm mt-1" data-testid="csv-name-mapping">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_auto">
                <span className="text-muted-foreground">
                  Auto: {detectedNameHeader || "use model #"}
                </span>
              </SelectItem>
              {columns.map((c) => (
                <SelectItem key={c.key} value={c.key}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">
            Row "Model #" comes from
          </Label>
          <Select
            value={modelOverrideKey || "_auto"}
            onValueChange={(v) => setModelOverrideKey(v === "_auto" ? "" : v)}
          >
            <SelectTrigger className="rounded-sm mt-1" data-testid="csv-model-mapping">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_auto">
                <span className="text-muted-foreground">
                  Auto: {detectedModelHeader || "(none)"}
                </span>
              </SelectItem>
              {columns.map((c) => (
                <SelectItem key={c.key} value={c.key}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground font-mono-data -mt-3">
        Effective: name ← {effectiveNameSrc} · model # ← {effectiveModelSrc}
      </div>

      {/* Spec sheet PDF */}
      <div>
        <Label className="text-xs uppercase tracking-wider mb-2 block">
          Default spec sheet PDF (optional)
        </Label>
        {specPdf ? (
          <div className="flex items-center gap-2 bg-slate-50 border border-border rounded-sm px-3 py-2 text-sm">
            <FileText className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="flex-1 truncate font-medium">
              {specPdf.filename}
            </span>
            <button
              onClick={onRemoveSpecPdf}
              className="p-1 text-muted-foreground hover:text-destructive"
              data-testid="csv-new-pt-pdf-remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 cursor-pointer border border-dashed border-border hover:border-amber-300 rounded-sm px-3 py-2 text-sm text-slate-700">
            {uploadingPdf ? (
              <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
            ) : (
              <Upload className="w-4 h-4 text-amber-600" />
            )}
            <span className="font-medium">
              {uploadingPdf ? "Uploading…" : "Upload spec sheet PDF"}
            </span>
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              disabled={uploadingPdf}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onPdfUpload(f);
                e.target.value = "";
              }}
              data-testid="csv-new-pt-pdf-input"
            />
          </label>
        )}
      </div>

      {/* Columns */}
      <div>
        <Label className="text-xs uppercase tracking-wider mb-2 block">
          Inferred columns ({columns.length})
        </Label>
        <div className="space-y-2">
          {columns.map((c, i) => {
            const meta = TYPE_META[c.type] || TYPE_META.text;
            const Icon = meta.icon;
            return (
              <div
                key={c.key}
                className="grid grid-cols-12 gap-2 items-center bg-white border border-border rounded-sm px-3 py-2"
                data-testid={`csv-new-pt-col-${i}`}
              >
                <Input
                  value={c.label}
                  onChange={(e) => setColumnLabel(i, e.target.value)}
                  className="col-span-5 rounded-sm h-9"
                />
                <Select
                  value={c.type}
                  onValueChange={(v) => setColumnType(i, v)}
                >
                  <SelectTrigger className="col-span-4 rounded-sm h-9">
                    <SelectValue>
                      <span className="inline-flex items-center gap-1.5">
                        <Icon className="w-3.5 h-3.5" />
                        {meta.label}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_META).map(([k, v]) => {
                      const VIcon = v.icon;
                      return (
                        <SelectItem key={k} value={k}>
                          <span className="inline-flex items-center gap-1.5">
                            <VIcon className="w-3.5 h-3.5" />
                            {v.label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <div className="col-span-2 text-[10px] text-muted-foreground font-mono-data truncate">
                  {(c.options && c.options.length > 0)
                    ? `${c.options.length} opts`
                    : ""}
                </div>
                <button
                  onClick={() => removeColumn(i)}
                  className="col-span-1 justify-self-end p-1 text-muted-foreground hover:text-destructive"
                  data-testid={`csv-new-pt-col-remove-${i}`}
                  title="Remove column"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview rows */}
      <div>
        <Label className="text-xs uppercase tracking-wider mb-2 block">
          Row preview ({Math.min(rows.length, 25)} of {rows.length})
        </Label>
        <div className="border border-border rounded-sm max-h-72 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white border-b border-border">
              <tr>
                <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">
                  Name
                </th>
                <th className="text-left px-2 py-1.5 font-medium text-muted-foreground">
                  Model #
                </th>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className="text-left px-2 py-1.5 font-medium text-muted-foreground"
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 25).map((r) => (
                <tr
                  key={r.line}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-2 py-1.5 font-medium">{r.name}</td>
                  <td className="px-2 py-1.5 font-mono-data">
                    {r.model_number || "—"}
                  </td>
                  {columns.map((c) => {
                    const v = r.attributes?.[c.key];
                    return (
                      <td key={c.key} className="px-2 py-1.5">
                        {Array.isArray(v)
                          ? v.join(", ")
                          : v === true
                          ? "Yes"
                          : v === false
                          ? "No"
                          : v ?? ""}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
